import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineHeart, HiHeart, HiOutlineShoppingBag, HiOutlineEye } from 'react-icons/hi';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const { addToCart } = useCart();
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { user } = useAuth();

  const isInWishlist = wishlist?.products?.some(item => item.product?._id === product._id) || false;

  const currentPrice = product.variants?.[selectedVariant]?.price || product.price;
  const comparePrice = product.variants?.[selectedVariant]?.compareAtPrice || product.compareAtPrice;
  const hasDiscount = comparePrice && comparePrice > currentPrice;
  const discountPercentage = hasDiscount ? Math.round((1 - currentPrice / comparePrice) * 100) : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const variant = product.variants?.[selectedVariant];
    if (!variant || variant.stock === 0) {
      toast.error(t('product.outOfStock'));
      return;
    }

    try {
      await addToCart(product._id, 1, variant.sku);
      toast.success(t('messages.addedToCart'));
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error(t('messages.pleaseLogin'));
      return;
    }

    try {
      if (isInWishlist) {
        await removeFromWishlist(product._id);
        toast.success(t('messages.removedFromWishlist'));
      } else {
        await addToWishlist(product._id);
        toast.success(t('messages.addedToWishlist'));
      }
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const isOutOfStock = product.variants?.every(v => v.stock === 0) || product.totalStock === 0;

  return (
    <div 
      className="group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/product/${product.slug}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
          {/* Main Image */}
          <img
            src={product.mainImage || product.images?.[0]?.url || '/placeholder.jpg'}
            alt={product.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              isHovered && product.images?.[1] ? 'opacity-0' : 'opacity-100'
            }`}
          />
          
          {/* Hover Image */}
          {product.images?.[1]?.url && (
            <img
              src={product.images[1].url}
              alt={product.name}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.isNew && (
              <span className="bg-luxury-charcoal text-white text-[10px] uppercase tracking-wider px-2 py-1">
                {t('product.new')}
              </span>
            )}
            {hasDiscount && (
              <span className="bg-luxury-gold text-white text-[10px] uppercase tracking-wider px-2 py-1">
                -{discountPercentage}%
              </span>
            )}
            {product.isBestSeller && (
              <span className="bg-primary-600 text-white text-[10px] uppercase tracking-wider px-2 py-1">
                {t('product.bestSeller')}
              </span>
            )}
            {isOutOfStock && (
              <span className="bg-gray-400 text-white text-[10px] uppercase tracking-wider px-2 py-1">
                {t('product.soldOut')}
              </span>
            )}
          </div>

          {/* Quick Actions */}
          <div className={`absolute top-3 right-3 flex flex-col gap-2 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
          }`}>
            <button
              onClick={handleWishlistToggle}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-luxury-gold hover:text-white transition-colors"
            >
              {isInWishlist ? (
                <HiHeart className="w-5 h-5 text-luxury-gold" />
              ) : (
                <HiOutlineHeart className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.href = `/product/${product.slug}`;
              }}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-luxury-gold hover:text-white transition-colors"
            >
              <HiOutlineEye className="w-5 h-5" />
            </button>
          </div>

          {/* Add to Cart Button */}
          {!isOutOfStock && (
            <div className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${
              isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <button
                onClick={handleAddToCart}
                className="w-full bg-white/95 backdrop-blur-sm text-luxury-charcoal py-3 text-xs uppercase tracking-widest hover:bg-luxury-charcoal hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <HiOutlineShoppingBag className="w-4 h-4" />
                Add to Cart
              </button>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="mt-4 text-center">
          {/* Category */}
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
            {product.category?.name}
          </p>

          {/* Name */}
          <h3 className="font-serif text-sm md:text-base mb-2 group-hover:text-luxury-gold transition-colors">
            {product.name}
          </h3>

          {/* Variant Colors */}
          {product.variants && product.variants.length > 1 && (
            <div className="flex items-center justify-center gap-1 mb-2">
              {product.variants.slice(0, 4).map((variant, index) => (
                <button
                  key={variant.sku}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedVariant(index);
                  }}
                  className={`w-4 h-4 rounded-full border-2 transition-all ${
                    selectedVariant === index 
                      ? 'border-luxury-gold scale-110' 
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: variant.colorHex || '#000' }}
                  title={variant.name}
                />
              ))}
              {product.variants.length > 4 && (
                <span className="text-xs text-gray-400">+{product.variants.length - 4}</span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium">{currentPrice.toFixed(2)} TND</span>
            {hasDiscount && (
              <span className="text-sm text-gray-400 line-through">{comparePrice.toFixed(2)} TND</span>
            )}
          </div>

          {/* Rating */}
          {product.averageRating > 0 && (
            <div className="flex items-center justify-center gap-1 mt-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-3 h-3 ${
                      star <= Math.round(product.averageRating)
                        ? 'text-luxury-gold'
                        : 'text-gray-300'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-xs text-gray-400">({product.reviewCount})</span>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
